// Firebase Storage service for ClubLink TT
import { storage } from './firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

/**
 * Upload a file to Firebase Storage
 * @param {File} file - The file to upload
 * @param {string} path - Storage path (e.g., 'clubs/proseriesfc/players/player123.jpg')
 * @returns {Promise<string>} - Download URL
 */
export const uploadFile = async (file, path) => {
  const storageRef = ref(storage, path);
  const snapshot = await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(snapshot.ref);
  return downloadURL;
};

/**
 * Upload an image with automatic resizing (optional)
 * @param {File} file - The image file
 * @param {string} path - Storage path
 * @param {number} maxWidth - Max width in pixels (optional)
 * @returns {Promise<string>} - Download URL
 */
export const uploadImage = async (file, path, maxWidth = null) => {
  // For now, upload directly (can add resizing later with canvas)
  return uploadFile(file, path);
};

/**
 * Delete a file from Firebase Storage
 * @param {string} path - Storage path or full URL
 */
export const deleteFile = async (path) => {
  try {
    // If it's a full URL, extract the path
    let storagePath = path;
    if (path.includes('firebasestorage.googleapis.com') || path.includes('firebasestorage.app')) {
      // Extract path from URL
      const url = new URL(path);
      const pathMatch = url.pathname.match(/\/o\/(.+?)(\?|$)/);
      if (pathMatch) {
        storagePath = decodeURIComponent(pathMatch[1]);
      }
    }
    
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  } catch (error) {
    // Ignore errors for files that don't exist
    if (error.code !== 'storage/object-not-found') {
      throw error;
    }
  }
};

/**
 * Generate a unique filename
 * @param {string} originalName - Original filename
 * @returns {string} - Unique filename with timestamp
 */
export const generateFilename = (originalName) => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  const extension = originalName.split('.').pop().toLowerCase();
  return `${timestamp}-${random}.${extension}`;
};

/**
 * Get storage path for different content types
 */
export const getStoragePath = {
  playerImage: (clubId, playerId) => `clubs/${clubId}/players/${playerId}`,
  productImage: (clubId, productId) => `clubs/${clubId}/products/${productId}`,
  galleryImage: (clubId, albumId, filename) => `clubs/${clubId}/gallery/${albumId}/${filename}`,
  playerDocument: (clubId, playerId, filename) => `clubs/${clubId}/players/${playerId}/documents/${filename}`,
  clubLogo: (clubId) => `clubs/${clubId}/logo`,
  clubBanner: (clubId) => `clubs/${clubId}/banner`,
};

/**
 * Check if a URL is a Firebase Storage URL
 */
export const isStorageUrl = (url) => {
  if (!url) return false;
  return url.includes('firebasestorage.googleapis.com') || 
         url.includes('firebasestorage.app') ||
         url.includes('storage.googleapis.com');
};

/**
 * Check if a URL is a base64 data URL
 */
export const isBase64Url = (url) => {
  if (!url) return false;
  return url.startsWith('data:');
};

export default {
  uploadFile,
  uploadImage,
  deleteFile,
  generateFilename,
  getStoragePath,
  isStorageUrl,
  isBase64Url,
};
